/**
 * Playback recovery policy — a pure reducer, so the rules are testable
 * without a media element.
 *
 *   - A URL that never started playing is skipped on a fatal error: try the
 *     next URL in `orderedUrls(station.urls)` straight away ("switching").
 *   - A URL that *was* playing and then dropped on a network error gets
 *     `MAX_RECONNECTS` retries with backoff ("reconnecting") before we give
 *     up on it — a radio stream on a phone switching Wi-Fi → cellular should
 *     come back, not hop to a different mirror.
 *   - While the device is offline we don't burn through URLs at all; we wait
 *     for the browser's `online` event ("offline").
 *   - With no URLs left the player is "failed" until the user retries.
 *
 * The `nonce` bumps whenever the current URL should be (re)mounted; the
 * player component uses it in its React key.
 */

export const RECONNECT_BACKOFF_MS = [1000, 2000, 4000, 8000, 15000] as const;
export const MAX_RECONNECTS = RECONNECT_BACKOFF_MS.length;

export type PlaybackStatus = "loading" | "playing" | "switching" | "reconnecting" | "offline" | "failed";

export interface PlaybackState {
  total: number;
  urlIndex: number;
  nonce: number;
  status: PlaybackStatus;
  /** Reconnect attempts spent on the current URL. */
  retries: number;
  /** Whether the current URL ever reached `playing`. */
  hasPlayed: boolean;
}

export type PlaybackEvent =
  | { type: "playing" }
  | { type: "fatal"; network: boolean; offline?: boolean }
  | { type: "retry" }
  | { type: "online" }
  | { type: "manualRetry" }
  | { type: "reset"; total: number };

export function initialPlayback(total: number): PlaybackState {
  return { total, urlIndex: 0, nonce: 0, status: total > 0 ? "loading" : "failed", retries: 0, hasPlayed: false };
}

export function playbackReducer(s: PlaybackState, e: PlaybackEvent): PlaybackState {
  switch (e.type) {
    case "reset":
      return initialPlayback(e.total);
    case "playing":
      return { ...s, status: "playing", retries: 0, hasPlayed: true };
    case "fatal": {
      if (s.status === "failed") return s;
      if (e.offline) return { ...s, status: "offline" };
      if (s.hasPlayed && e.network && s.retries < MAX_RECONNECTS) {
        return { ...s, status: "reconnecting", retries: s.retries + 1 };
      }
      if (s.urlIndex + 1 < s.total) {
        return { ...s, urlIndex: s.urlIndex + 1, nonce: s.nonce + 1, status: "switching", retries: 0, hasPlayed: false };
      }
      return { ...s, status: "failed" };
    }
    case "retry":
      // Fired by the backoff timer while reconnecting — remount the same URL.
      return s.status === "reconnecting" ? { ...s, nonce: s.nonce + 1 } : s;
    case "online":
      if (s.status !== "offline" && s.status !== "failed" && s.status !== "reconnecting") return s;
      return { ...s, nonce: s.nonce + 1, status: "loading", retries: 0 };
    case "manualRetry":
      return { ...initialPlayback(s.total), nonce: s.nonce + 1 };
  }
}

/** Backoff before reconnect attempt `retries` (1-based). */
export function reconnectDelay(retries: number): number {
  const i = Math.min(Math.max(retries, 1), RECONNECT_BACKOFF_MS.length) - 1;
  return RECONNECT_BACKOFF_MS[i]!;
}
